<template>
  <div class="container mt-5 invoice-page">
    <smartsupp-live-chat/>
    <h1 class="page-title"><b>{{ $t('invoice_page.page_title') }}</b></h1>
    <sc-card class="basic-info-card mb-5" v-if="invoice">
      <div slot="content">
        <div class="position-relative head">
          <i class="paid sc-icon-checkbox-checked" v-if="invoice.state === InvoiceState.PAID"></i>
          <h4 class="paid d-inline ml-3" v-if="invoice.state === InvoiceState.PAID">
            {{ $t('invoice_page.paid') }}
          </h4>
          <h4 class="d-inline" v-if="invoice.state === InvoiceState.UNPAID">
            {{ $t('invoice_page.unpaid') }}
          </h4>
          <span class="text-dark position-absolute r-0 b-0">
            {{ $t('invoice_page.id', { id: invoice.textualId }) }}
          </span>
        </div>
        <div class="row mt-5">
          <div class="col-3 bold">{{ $t('invoice_page.issue_date') }}</div>
          <div class="col-3 bold">{{ $t('invoice_page.due_date') }}</div>
          <div class="col-3 bold">{{ invoice.paymentDate ? $t('invoice_page.payment_date') : '' }}</div>
          <div class="col-3 bold">{{ invoice.completionDate ? $t('invoice_page.completion_date') : '' }}</div>
          <div class="col-3">{{ invoice.issueDate | date }}</div>
          <div class="col-3">{{ invoice.dueDate | date }}</div>
          <div class="col-3">{{ invoice.paymentDate | date }}</div>
          <div class="col-3">{{ invoice.completionDate | date }}</div>
        </div>
        <table class="details max-width mt-5">
          <thead>
            <th>{{ $t('invoice_page.service') }}</th>
            <th>{{ $t('invoice_page.description') }}</th>
            <th class="text-center">{{ $t('invoice_page.net_price') }}</th>
            <th class="text-center">{{ $t('invoice_page.vat') }}</th>
            <th class="payment-sum text-center">{{ $t('invoice_page.gross_price') }}</th>
          </thead>
          <tbody>
            <tr v-for="item in invoice.items" :key="item.id">
              <td>{{ $t('invoice_page.service_package_name.' + item.serviceName) }}</td>
              <td>{{ $dateRange(splitDates(item.description)[0], splitDates(item.description)[1]) }}</td>
              <td class="text-center">{{ $nonRoundedPrice(item.netPrice * item.quantity) }}</td>
              <td class="text-center">{{ $nonRoundedPrice(item.vat * item.quantity) }}</td>
              <td class="payment-sum text-center">{{ $nonRoundedPrice((item.netPrice + item.vat) * item.quantity) }}</td>
            </tr>
            <tr class="spacer">
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td></td>
              <td class="text-right"><b>{{ $t('invoice_page.total_amount') }}</b></td>
              <td class="text-center">{{ $price(invoice.amount) }}</td>
              <td class="text-center">{{ $price(invoice.grossAmount - invoice.amount) }}</td>
              <td class="payment-sum text-center bold">{{ $price(invoice.grossAmount) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </sc-card>
    <sc-card class="payment-method payment-cart mb-5 max-width" v-if="invoice && invoice.state !== InvoiceState.PAID">
      <div slot="title" class="title">
        {{ $t('invoice_page.payment_method.pick_payment_method') }}
      </div>
      <div slot="content" class="content">
        <div>
          <ScRadioInput class="radio-element">
            <input
              type="radio"
              :value="PaymentMethods.OTP"
              id="card"
              name="paymentMethod"
              v-model="paymentMethod">
            <label for="card">
              {{ $t('invoice_page.payment_method.credit_card') }}
              <a :href="$t('invoice_page.payment_method.simple_pay_payment_info_url')" target="_blank">
                <img height="27" src="../../../public/img/simplepay_horizontal_02.png" :title="$t('payment.simple_pay.logo.title')" :alt="$t('payment.simple_pay.logo.alt')">
              </a>
            </label>
          </ScRadioInput>
          <ScRadioInput class="radio-element">
            <input
              type="radio"
              id="wire"
              :value="PaymentMethods.WIRE"
              name="paymentMethod"
              v-model="paymentMethod">
            <label for="wire">{{ $t('invoice_page.payment_method.wire') }}
            </label>
          </ScRadioInput>
        </div>
        <div v-if="paymentMethod === PaymentMethods.WIRE" class="mt-3">
          {{ $t('invoice_page.wire.info') }}
          <div class="wiretransfer row col-6 offset-3 mt-5">
            <div class="col-6 p-0">
              {{ $t('invoice_page.wire.to') }}
            </div>
            <div class="col-6 p-0">
              {{ payment.wiretransfer.to }}
            </div>
            <hr class="max-width my-2">
            <div class="col-6 p-0">
              {{ $t('invoice_page.wire.bank') }}
            </div>
            <div class="col-6 p-0">
              {{ payment.wiretransfer.bank }}
            </div>
            <hr class="max-width my-2">
            <div class="col-6 p-0">
              {{ $t('invoice_page.wire.account_number') }}
            </div>
            <div class="col-6 p-0">
              {{ payment.wiretransfer.accountNumber }}
            </div>
            <hr class="max-width my-2">
            <div class="col-6 p-0">
              {{ $t('invoice_page.wire.total_amount') }}
            </div>
            <div class="col-6 p-0">
              {{ $price(invoice.grossAmount) }}
            </div>
            <hr class="max-width my-2">
            <div class="col-6 p-0">
              {{ $t('invoice_page.wire.invoice_id') }}
            </div>
            <div class="col-6 p-0">
              {{ invoice.textualId }}
            </div>
            <hr class="max-width my-2">
          </div>
        </div>
      </div>
    </sc-card>

    <div v-if="invoice && paymentMethod === PaymentMethods.OTP && invoice.state !== InvoiceState.PAID" class="text-center">
      <a :href="payment.bankcard.actionUrl" class="no-text-decoration">
        <rounded-button class="col-3" color="primary">
          <span>{{ $t('common.next') }}</span>
        </rounded-button>
      </a>
    </div>
  </div>
</template>

<script>
import { ScCard, ScRadioInput } from 'smartcommerce-component-library'
import { PaymentMethods } from '@/shared/utils/PaymentMethods'
import { InvoiceState } from '../utils/InvoiceState'
import { Routes } from '../../order-wizard/utils/Routes'
import { webshopService } from '../../shared/services/WebshopService'
import SmartsuppLiveChat from '../../shared/components/SmartsuppLiveChat'

export default {
  name: 'InvoicePage',
  components: { ScCard, ScRadioInput, SmartsuppLiveChat },
  data () {
    return {
      PaymentMethods,
      paymentMethod: PaymentMethods.OTP,
      invoice: null,
      InvoiceState,
      payment: null
    }
  },

  methods: {
    splitDates (dates) {
      return [dates.substr(0, 10), dates.substr(11, 10)]
    }
  },

  async created () {
    const invoiceId = this.$route.params.invoiceId
    const key = this.$route.params.key

    if (!invoiceId || !key) {
      this.$router.push(Routes.home())
      return
    }

    try {
      const resp = await webshopService.getInvoice({ invoiceId, key })
      this.invoice = resp.invoice
      this.payment = resp.payment
    } catch (e) {
      this.$router.push(Routes.home())
    }
  }
}
</script>

<style lang="scss" scoped>
.head {
  .paid {
    color: $sc-primary;
  }

  span {
    font-size: 1rem;
    font-weight: 100;
    vertical-align: baseline;
  }

  i {
    font-size: 32px;
    top: 3px;
    position: relative;
  }
}
.basic-info-card {
  width: 100%;
  padding: 9px;
}

.bold {
  font-weight: 500;
}

table.details {
  width: 100%;
  line-height: 30px;

  th {
    border-bottom: 2px rgb(219, 217, 217) solid;
  }

  tr {
    &.sum {
      &.total-cost {
        line-height: 50px;
      }
    }

    &.total-cost {
      font-weight: 800;
    }
  }

  tr.spacer {
    border-top: 2px solid $primary;
  }

  .payment-sum {
    background: rgba(89,153,0,.08);
    border-bottom-color: rgba(89,153,0,.08);
    font-weight: 800;
  }

  th.payment-sum {
    border-bottom-color: rgba(89,153,0,.08);
  }
}

.payment-cart {
  margin-bottom: 36px;
  padding: 20px;
}
/deep/ .radio-element {
  label::before {
    top: 2px;
    line-height: 10px !important;
  }
}

.wiretransfer {
  font-family: 'Work Sans', sans-serif;
  -webkit-font-smoothing: antialiased;
}

.container.invoice-page {
  padding-bottom: 25px;
}
</style>
